import { z } from 'zod';

/**
 * Common Domain Value Objects
 * Shared domain value objects used across different pools
 */

// === Common ID Value Objects ===

export const JobIdSchema = z.string().cuid({
  message: "Invalid Job ID format. Must be a valid CUID."
});

// === Common String Value Objects ===

export const ErrorMessageSchema = z.string()
  .max(5000, { message: "Error message must be less than 5000 characters" })
  .nullable()
  ;

export const SearchQuerySchema = z.string()
  .min(1, { message: "Search query cannot be empty" })
  .max(500, { message: "Search query must be less than 500 characters" })
  ;

// === Common Numeric Value Objects ===

export const CreditsUsedSchema = z.number()
  .int()
  .min(0, { message: "Credits used cannot be negative" })
  ;

export const ExecutionTimeSchema = z.number()
  .int()
  .min(0, { message: "Execution time cannot be negative" })
  .nullable()
  ;

export const PrioritySchema = z.number()
  .int()
  .min(0, { message: "Priority cannot be negative" })
  .max(10, { message: "Priority cannot exceed 10" })
  ;

export const RetryCountSchema = z.number()
  .int()
  .min(0, { message: "Retry count cannot be negative" })
  ;

export const MaxRetriesSchema = z.number()
  .int()
  .min(0, { message: "Max retries cannot be negative" })
  .max(10, { message: "Max retries cannot exceed 10" })
  ;

export const LimitSchema = z.number()
  .int()
  .min(1, { message: "Limit must be at least 1" })
  .max(1000, { message: "Limit cannot exceed 1000" })
  ;

export const OffsetSchema = z.number()
  .int()
  .min(0, { message: "Offset cannot be negative" })
  ;

export const CountSchema = z.number()
  .int()
  .min(0, { message: "Count cannot be negative" })
  ;

// === Common Boolean Value Objects ===

export const IsPublicSchema = z.boolean();
export const HasMoreSchema = z.boolean();

// === Common Complex Value Objects ===

export const JobInputSchema = z.record(z.string(), z.unknown())
  ;

export const JobOutputSchema = z.record(z.string(), z.unknown())
  .nullable()
  ;

// Export TypeScript types
export type JobId = z.infer<typeof JobIdSchema>;
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;
export type SearchQuery = z.infer<typeof SearchQuerySchema>;
export type CreditsUsed = z.infer<typeof CreditsUsedSchema>;
export type ExecutionTime = z.infer<typeof ExecutionTimeSchema>;
export type Priority = z.infer<typeof PrioritySchema>;
export type RetryCount = z.infer<typeof RetryCountSchema>;
export type MaxRetries = z.infer<typeof MaxRetriesSchema>;
export type Limit = z.infer<typeof LimitSchema>;
export type Offset = z.infer<typeof OffsetSchema>;
export type Count = z.infer<typeof CountSchema>;
export type IsPublic = z.infer<typeof IsPublicSchema>;
export type HasMore = z.infer<typeof HasMoreSchema>;
export type JobInput = z.infer<typeof JobInputSchema>;
export type JobOutput = z.infer<typeof JobOutputSchema>;